/**
 * /settings → Prullenbak.
 *
 * Sinds 2026-05-21 member-toegankelijk: non-admins zien alleen eigen
 * weggegooi-items (server-side filter), admins zien alles. Acties:
 *   - **Herstellen**: zet `tombstone_in_branch` op NULL. De server
 *     pakt automatisch alle tombstoned relaties mee waarvan source
 *     of target in de te-herstellen-set zit (mits andere endpoint
 *     leeft; anders skip + report).
 *   - **Definitief verwijderen** (admin-only): hard-delete via
 *     `/admin/trash/purge`. Cascading FKs op `relations.source/target`
 *     zorgen dat restanten netjes meegaan.
 *
 * Per entry tonen we ook op welke (live) diagrams het object stond
 * vóór verwijdering, zodat de gebruiker meteen ziet waar 't terugkomt
 * bij restore. Na een succesvolle restore-actie hydraten we de
 * workspace-store zodat open diagrammen het object weer rendert
 * zonder dat de gebruiker hoeft te refreshen.
 *
 * Items zijn gegroepeerd per "delete-event" (alle items met dezelfde
 * `tombstoned_by` binnen een 60s-window) zodat een batch-verwijdering
 * vanaf de canvas als één blok in de UI verschijnt.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type TrashItem } from "../api/client";
import { resolveBilingual } from "../registry";
import { useSession } from "../state/session";
import { useWorkspaceData } from "../state/workspace";
import { ld, pill } from "./ledgerUi";

interface Props {
  workspaceId: string;
}

interface Group {
  key: string;
  who: string;
  /** Tijdstempel van het oudste item in de groep (ISO). */
  when: string;
  items: TrashItem[];
}

export function TrashSettings({ workspaceId }: Props) {
  const { t } = useTranslation();
  const branchId = useSession((s) => s.branchId);
  const [items, setItems] = useState<TrashItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [olderThanDate, setOlderThanDate] = useState<string>(""); // YYYY-MM-DD

  const refresh = useCallback(async () => {
    if (!branchId) return;
    setError(null);
    try {
      const res = await api.listTrash(workspaceId, branchId);
      setItems(res.items);
      // Selection bijwerken — alleen ids die nog bestaan behouden.
      setSelected((prev) => {
        const live = new Set(res.items.map(itemKey));
        const next = new Set<string>();
        for (const k of prev) if (live.has(k)) next.add(k);
        return next;
      });
    } catch (err) {
      setError(explain(err));
    }
  }, [workspaceId, branchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const groups = useMemo(() => groupByEvent(items), [items]);

  const toggleItem = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggleGroup = (group: Group) =>
    setSelected((prev) => {
      const keys = group.items.map(itemKey);
      const allSelected = keys.every((k) => prev.has(k));
      const next = new Set(prev);
      if (allSelected) {
        for (const k of keys) next.delete(k);
      } else {
        for (const k of keys) next.add(k);
      }
      return next;
    });

  const selectionIds = useMemo(() => {
    const conceptIds: string[] = [];
    const relationIds: string[] = [];
    for (const item of items) {
      if (!selected.has(itemKey(item))) continue;
      if (item.kind === "concept") conceptIds.push(item.id);
      else relationIds.push(item.id);
    }
    return { conceptIds, relationIds };
  }, [items, selected]);

  const selectionCount = selectionIds.conceptIds.length + selectionIds.relationIds.length;

  const restoreSelection = async () => {
    if (selectionCount === 0) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (!branchId) return;
      const res = await api.restoreTrash(workspaceId, branchId, {
        concept_ids: selectionIds.conceptIds,
        relation_ids: selectionIds.relationIds,
      });
      const parts: string[] = [];
      if (res.concepts_restored > 0)
        parts.push(t("settings.trash.restoreConcepts", { count: res.concepts_restored }));
      if (res.relations_restored > 0)
        parts.push(t("settings.trash.restoreRelations", { count: res.relations_restored }));
      const msg =
        parts.length === 0
          ? t("settings.trash.restoreEmpty")
          : t("settings.trash.restoreSuccess", { parts: parts.join(", ") });
      const skip = res.relations_skipped_dangling.length;
      setInfo(skip > 0 ? t("settings.trash.restoreSkipped", { base: msg, count: skip }) : msg);
      setSelected(new Set());
      await refresh();
      // Hydrate de workspace-store zodat de openstaande diagrammen het
      // herstelde concept/relatie meteen weer renderen — geen page-
      // refresh nodig. ViewState-entries van diagrammen die intussen
      // óók verwijderd zijn vallen automatisch weg omdat hydrate alleen
      // live diagrams meeneemt.
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const purgeSelection = async () => {
    if (selectionCount === 0) return;
    if (!confirm(t("settings.trash.purgeConfirm", { count: selectionCount }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (!branchId) return;
      const res = await api.purgeTrash(workspaceId, branchId, {
        concept_ids: selectionIds.conceptIds,
        relation_ids: selectionIds.relationIds,
      });
      setInfo(
        t("settings.trash.purgedToast", {
          concepts: res.concepts_purged,
          relations: res.relations_purged,
        }),
      );
      setSelected(new Set());
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const purgeOlderThan = async () => {
    if (!olderThanDate) return;
    if (!confirm(t("settings.trash.purgeOlderConfirm", { date: olderThanDate }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      // ISO-string op begin van die dag — items met een eerder updated_at
      // worden gepurged. SQLite vergelijkt strings, en updated_at staat
      // als 'YYYY-MM-DD HH:MM:SS' opgeslagen, dus YYYY-MM-DDT00:00:00Z
      // werkt alfabetisch correct (Z-suffix sorteert ná spaties).
      const cutoff = `${olderThanDate} 00:00:00`;
      if (!branchId) return;
      const res = await api.purgeTrash(workspaceId, branchId, { older_than: cutoff });
      setInfo(
        t("settings.trash.purgedOlderToast", {
          concepts: res.concepts_purged,
          relations: res.relations_purged,
          date: olderThanDate,
        }),
      );
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const purgeAll = async () => {
    if (items.length === 0) return;
    if (!confirm(t("settings.trash.purgeAllConfirm", { count: items.length }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (!branchId) return;
      const res = await api.purgeTrash(workspaceId, branchId, { confirm: true });
      setInfo(
        t("settings.trash.purgedAllToast", {
          concepts: res.concepts_purged,
          relations: res.relations_purged,
        }),
      );
      setSelected(new Set());
      await refresh();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="flex flex-col gap-5">
      <p className={ld.viewIntro}>
        <span className="font-medium text-(--ld-text)">{t("settings.trash.introBoldTitle")}</span>
        {t("settings.trash.introAfter")} {t("settings.trash.introDetail")}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void restoreSelection()}
          disabled={busy || selectionCount === 0}
          className={ld.primary}
        >
          {t("settings.trash.restoreButton", { count: selectionCount })}
        </button>
        <button
          type="button"
          onClick={() => void purgeSelection()}
          disabled={busy || selectionCount === 0}
          className={ld.danger}
        >
          {t("settings.trash.purgeSelectionButton", { count: selectionCount })}
        </button>
        <button
          type="button"
          onClick={() => void refresh()}
          disabled={busy}
          className={`ml-auto ${ld.textButton}`}
        >
          {t("settings.trash.refresh")}
        </button>
      </div>

      <div aria-live="polite">
        {error && (
          <p role="alert" className={ld.error}>
            {error}
          </p>
        )}
        {info && <p className={ld.success}>{info}</p>}
      </div>

      {items.length === 0 ? (
        <p className={`${ld.well} text-(--ld-muted) text-sm`}>{t("settings.trash.empty")}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((group) => (
            <GroupCard
              key={group.key}
              group={group}
              selected={selected}
              onToggleGroup={() => toggleGroup(group)}
              onToggleItem={toggleItem}
            />
          ))}
        </div>
      )}

      {/* Opruimen in bulk: onomkeerbaar, dus onderaan en apart. */}
      <div className="mt-3 border-(--ld-rule) border-t pt-6">
        <h3 className={ld.sectionTitle}>{t("settings.trash.cleanupTitle")}</h3>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label htmlFor="trash-older-than" className={ld.label}>
              {t("settings.trash.olderThanLabel")}
            </label>
            <input
              id="trash-older-than"
              type="date"
              value={olderThanDate}
              onChange={(e) => setOlderThanDate(e.target.value)}
              className={ld.input}
              disabled={busy}
            />
          </div>
          <button
            type="button"
            onClick={() => void purgeOlderThan()}
            disabled={busy || !olderThanDate}
            className={ld.danger}
          >
            {t("settings.trash.purgeOlderButton")}
          </button>
          <button
            type="button"
            onClick={() => void purgeAll()}
            disabled={busy || items.length === 0}
            className={`sm:ml-auto ${ld.danger}`}
            title={t("settings.trash.purgeAllTitle")}
          >
            {t("settings.trash.purgeAllButton")}
          </button>
        </div>
      </div>
    </section>
  );
}

const checkboxClass = "h-4 w-4 shrink-0 accent-(--ld-primary)";

function GroupCard({
  group,
  selected,
  onToggleGroup,
  onToggleItem,
}: {
  group: Group;
  selected: Set<string>;
  onToggleGroup: () => void;
  onToggleItem: (key: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const allSelected = group.items.every((it) => selected.has(itemKey(it)));
  const someSelected = !allSelected && group.items.some((it) => selected.has(itemKey(it)));

  // Multi-delete (>1 item) start collapsed met een uitklapknop. Single-item
  // groepen blijven altijd open — geen meerwaarde voor een collapse-knop.
  const isMulti = group.items.length > 1;
  const [expanded, setExpanded] = useState(!isMulti);

  // Relaties onder hun concept hangen wanneer source/target in dezelfde
  // groep zit; rest staat standalone onderaan.
  const nested = useMemo(() => nestRelationsUnderConcepts(group.items), [group.items]);

  return (
    <div className="rounded-lg border border-(--ld-border) bg-(--ld-surface)">
      <header className="flex items-center justify-between gap-3 px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <input
            type="checkbox"
            className={checkboxClass}
            aria-label={t("settings.trash.selectGroup", { when: formatTimestamp(group.when) })}
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={onToggleGroup}
          />
          {isMulti ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex min-w-0 items-center gap-2 rounded-sm text-left text-sm"
              title={expanded ? t("settings.trash.collapseGroup") : t("settings.trash.expandGroup")}
              aria-expanded={expanded}
            >
              <span aria-hidden="true" className="w-3 text-(--ld-muted)">
                {expanded ? "▾" : "▸"}
              </span>
              <span className="font-medium tabular-nums">{formatTimestamp(group.when)}</span>
              <span className="truncate text-(--ld-muted)">{group.who}</span>
            </button>
          ) : (
            <span className="flex min-w-0 items-center gap-2 text-sm">
              <span className="w-3" />
              <span className="font-medium tabular-nums">{formatTimestamp(group.when)}</span>
              <span className="truncate text-(--ld-muted)">{group.who}</span>
            </span>
          )}
        </div>
        <span className="shrink-0 text-(--ld-muted) text-xs tabular-nums">
          {t("settings.trash.itemsCount", { count: group.items.length })}
        </span>
      </header>
      {expanded && (
        <ul className="divide-y divide-(--ld-rule) border-(--ld-rule) border-t">
          {nested.concepts.map(({ concept, nestedRelations }) => (
            <li key={itemKey(concept)} className="py-2 pr-4 pl-10">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className={`${checkboxClass} mt-0.5`}
                  aria-label={
                    resolveBilingual(concept.name, i18n.language) || t("settings.trash.noName")
                  }
                  checked={selected.has(itemKey(concept))}
                  onChange={() => onToggleItem(itemKey(concept))}
                />
                <ConceptRow item={concept} />
              </div>
              {nestedRelations.length > 0 && (
                <ul className="mt-2 ml-7 flex flex-col gap-2 border-(--ld-border) border-l pl-4">
                  {nestedRelations.map((rel) => (
                    <li key={itemKey(rel)} className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className={`${checkboxClass} mt-0.5`}
                        aria-label={t("settings.trash.kindRelation")}
                        checked={selected.has(itemKey(rel))}
                        onChange={() => onToggleItem(itemKey(rel))}
                      />
                      <RelationRow item={rel} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {nested.orphanRelations.map((rel) => (
            <li key={itemKey(rel)} className="flex items-start gap-3 py-2 pr-4 pl-10">
              <input
                type="checkbox"
                className={`${checkboxClass} mt-0.5`}
                aria-label={t("settings.trash.kindRelation")}
                checked={selected.has(itemKey(rel))}
                onChange={() => onToggleItem(itemKey(rel))}
              />
              <RelationRow item={rel} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConceptRow({ item }: { item: Extract<TrashItem, { kind: "concept" }> }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {resolveBilingual(item.name, i18n.language) || t("settings.trash.noName")}
        </span>
        <span className={pill.neutral}>{t("settings.trash.kindConcept")}</span>
        <span className="text-(--ld-muted) text-xs">{shortType(item.type_id)}</span>
      </div>
      <PlacedOnDiagrams diagrams={item.placed_on_diagrams} />
    </div>
  );
}

function RelationRow({ item }: { item: Extract<TrashItem, { kind: "relation" }> }) {
  const { t, i18n } = useTranslation();
  const labelText = resolveBilingual(item.label, i18n.language);
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className={labelText ? "font-medium" : "text-(--ld-muted)"}>
          {labelText || t("settings.trash.noLabel")}
        </span>
        <span className={pill.neutral}>{t("settings.trash.kindRelation")}</span>
        <span className="text-(--ld-muted) text-xs">{shortType(item.type_id)}</span>
        {item.source_or_target_tombstoned && (
          <span className={pill.notice} title={t("settings.trash.danglingTitle")}>
            {t("settings.trash.danglingBadge")}
          </span>
        )}
      </div>
      <PlacedOnDiagrams diagrams={item.placed_on_diagrams} />
    </div>
  );
}

/** Toont onder een trash-row een compacte regel met de live diagrammen
 *  waarop dit item stond vóór verwijdering. Tombstoned diagrams zitten
 *  hier niet in — die filtert de server al weg. Lege array → niets. */
function PlacedOnDiagrams({ diagrams }: { diagrams: TrashItem["placed_on_diagrams"] }) {
  const { t, i18n } = useTranslation();
  if (!diagrams || diagrams.length === 0) return null;
  const names = diagrams
    .map((d) => resolveBilingual(d.name, i18n.language) || t("settings.trash.noName"))
    .join(" · ");
  return (
    <div className="text-(--ld-muted) text-xs leading-5">
      {t("settings.trash.placedOn")}: {names}
    </div>
  );
}

function shortType(typeId: string): string {
  // Strip de pack-prefix ("archimate.", "c4.", "drawing.") voor compactere
  // weergave; admins kennen de packs wel.
  return typeId.includes(".") ? typeId.split(".").slice(1).join(".") : typeId;
}

function itemKey(item: TrashItem): string {
  return `${item.kind}:${item.id}`;
}

/** Groepeer items van dezelfde gebruiker binnen een 60-seconden-window. */
/** Groepeer items per delete-actie. Voorkeur:
 *  1. `delete_action_id` (server-side gezet bij bulk-delete sinds
 *     migration 0032) — strikte groepering.
 *  2. Fallback: zelfde-user-binnen-60s — voor pre-migration tombstones
 *     of single-delete-calls die geen action_id meegaven.
 *  Sortering: nieuwste groep eerst (op laatste item in de groep). */
function groupByEvent(items: TrashItem[]): Group[] {
  const sorted = [...items].sort((a, b) => b.tombstoned_at.localeCompare(a.tombstoned_at));

  // 1. Items met action_id eerst per action_id bundelen.
  const byActionId = new Map<string, Group>();
  const noActionId: TrashItem[] = [];
  for (const item of sorted) {
    if (item.delete_action_id) {
      let g = byActionId.get(item.delete_action_id);
      if (!g) {
        g = {
          key: `action:${item.delete_action_id}`,
          who: item.tombstoned_by,
          when: item.tombstoned_at,
          items: [],
        };
        byActionId.set(item.delete_action_id, g);
      }
      g.items.push(item);
      if (item.tombstoned_at < g.when) g.when = item.tombstoned_at;
    } else {
      noActionId.push(item);
    }
  }

  // 2. Rest: 60s-window-heuristiek.
  const fallbackGroups: Group[] = [];
  for (const item of noActionId) {
    const lastGroup = fallbackGroups[fallbackGroups.length - 1];
    const sameUser = lastGroup && lastGroup.who === item.tombstoned_by;
    const closeInTime =
      lastGroup && Math.abs(parseTs(lastGroup.when) - parseTs(item.tombstoned_at)) <= 60_000;
    if (lastGroup && sameUser && closeInTime) {
      lastGroup.items.push(item);
      if (item.tombstoned_at < lastGroup.when) lastGroup.when = item.tombstoned_at;
    } else {
      fallbackGroups.push({
        key: `fallback:${item.tombstoned_by}-${item.tombstoned_at}-${fallbackGroups.length}`,
        who: item.tombstoned_by,
        when: item.tombstoned_at,
        items: [item],
      });
    }
  }

  // 3. Combineer + sorteer op nieuwste-eerst (max tombstoned_at per groep).
  const all = [...byActionId.values(), ...fallbackGroups];
  all.sort((a, b) => {
    const maxA = a.items.reduce((acc, it) => (it.tombstoned_at > acc ? it.tombstoned_at : acc), "");
    const maxB = b.items.reduce((acc, it) => (it.tombstoned_at > acc ? it.tombstoned_at : acc), "");
    return maxB.localeCompare(maxA);
  });
  return all;
}

/** Splits items binnen één groep in concepts + per-concept-geneste
 *  relaties + orphan relaties (relaties zonder bijbehorende concept
 *  in deze groep). Volgorde: concepts in originele volgorde, daarna
 *  orphan relaties (= alle relaties waar source en target ofwel niet
 *  in deze groep zitten, ofwel niet bestaan). */
export interface NestedGroupView {
  concepts: Array<{
    concept: Extract<TrashItem, { kind: "concept" }>;
    nestedRelations: Array<Extract<TrashItem, { kind: "relation" }>>;
  }>;
  orphanRelations: Array<Extract<TrashItem, { kind: "relation" }>>;
}

function nestRelationsUnderConcepts(items: TrashItem[]): NestedGroupView {
  const concepts = items.filter(
    (i): i is Extract<TrashItem, { kind: "concept" }> => i.kind === "concept",
  );
  const relations = items.filter(
    (i): i is Extract<TrashItem, { kind: "relation" }> => i.kind === "relation",
  );
  const conceptIds = new Set(concepts.map((c) => c.id));
  const nestedById = new Map<string, Array<Extract<TrashItem, { kind: "relation" }>>>();
  const orphan: Array<Extract<TrashItem, { kind: "relation" }>> = [];
  for (const rel of relations) {
    // Hang onder de eerste matching concept-id (source heeft voorrang).
    let parentId: string | null = null;
    if (conceptIds.has(rel.source_concept_id)) parentId = rel.source_concept_id;
    else if (conceptIds.has(rel.target_concept_id)) parentId = rel.target_concept_id;
    if (parentId) {
      let arr = nestedById.get(parentId);
      if (!arr) {
        arr = [];
        nestedById.set(parentId, arr);
      }
      arr.push(rel);
    } else {
      orphan.push(rel);
    }
  }
  return {
    concepts: concepts.map((c) => ({
      concept: c,
      nestedRelations: nestedById.get(c.id) ?? [],
    })),
    orphanRelations: orphan,
  };
}

function parseTs(ts: string): number {
  // SQLite gebruikt 'YYYY-MM-DD HH:MM:SS' (UTC). Voeg Z toe voor Date.
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  return Date.parse(iso);
}

function formatTimestamp(ts: string): string {
  const ms = parseTs(ts);
  if (!Number.isFinite(ms)) return ts;
  return new Date(ms).toLocaleString();
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return "Unknown error";
}
